import {
    type IndustryCodeDto,
    industryCodeDtoMap,
    type IndustryCodes,
} from "app/pages/sales-intelligence/domain/industry/types/codes";
import type { IIndustryCodesFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterPanelBaseFilterDeps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type { FilterValueLifecycleStatus } from "app/pages/sales-intelligence/domain/inclusion/types";
import {
    calculateInclusionConditionsScalarDelta,
    enforceAtMostOneExcludeCondition,
    isExcludeInclusionAvailable,
    isInclusionConditionFullyRemoved,
} from "app/pages/sales-intelligence/domain/inclusion/inclusionFilterConditionUtils";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import { hasSameIndustryCodesObject } from "app/pages/sales-intelligence/domain/industry/utils/operations";

import { normalizeInclusionConditionArrayValue } from "../../utils/normalizeInclusionConditionArrayValue";

import { IndustryCodesFilterValue } from "./IndustryCodesFilterValue";

const getIndustryCodesConditionScalars = (item: IIndustryCodesFilterDto): readonly string[] => [
    ...item.naicsCodes.map((code) => `naics:${code}`),
    ...item.sicCodes.map((code) => `sic:${code}`),
];

type IndustryCodesFilterOptions = {
    readonly inclusionOptions: readonly InclusionOption[];
    readonly naicsCode: readonly IndustryCodes[];
    readonly sicCodes: readonly IndustryCodes[];
};

class IndustryCodesFilterModel extends FilterPanelBaseFilter<IIndustryCodesFilterDto[], IndustryCodesFilterOptions> {
    readonly types: readonly InclusionOption[];
    readonly codeOptions: readonly IndustryCodeDto[] = Object.values(industryCodeDtoMap);

    constructor(deps: IFilterPanelBaseFilterDeps<IIndustryCodesFilterDto[], IndustryCodesFilterOptions>) {
        super(deps);

        this.types = deps.options.inclusionOptions;
    }

    isExcludeInclusionAvailable(
        conditions?: IIndustryCodesFilterDto[],
        editing?: IIndustryCodesFilterDto | null,
    ): boolean {
        return isExcludeInclusionAvailable(this.fromApiValue(conditions), editing ?? null);
    }

    fromApiValue(value?: readonly IIndustryCodesFilterDto[] | IIndustryCodesFilterDto): IIndustryCodesFilterDto[] {
        return normalizeInclusionConditionArrayValue(value);
    }

    toApiValue(value?: IIndustryCodesFilterDto[]): IIndustryCodesFilterDto[] | undefined {
        const normalizedValue = this.fromApiValue(value);
        const filteredValue = enforceAtMostOneExcludeCondition(
            normalizedValue.filter((item) => !new IndustryCodesFilterValue(item).isEmpty()),
        );

        return filteredValue.length ? filteredValue : undefined;
    }

    isValueEmpty(value?: readonly IIndustryCodesFilterDto[] | IIndustryCodesFilterDto): boolean {
        const normalizedValue = this.fromApiValue(value);

        return !normalizedValue.length || normalizedValue.every((item) => new IndustryCodesFilterValue(item).isEmpty());
    }

    calculateValueDelta(
        previousValue: IIndustryCodesFilterDto[] | undefined,
        newValue: IIndustryCodesFilterDto[] | undefined,
    ): FilterValueDelta<string> {
        return calculateInclusionConditionsScalarDelta(
            this.fromApiValue(previousValue),
            this.fromApiValue(newValue),
            getIndustryCodesConditionScalars,
            hasSameIndustryCodesObject,
        );
    }

    getValueLifecycleStatus<V = IIndustryCodesFilterDto[]>(
        value?: V,
        valueDelta?: FilterValueDelta<V>,
    ): FilterValueLifecycleStatus {
        const condition = value as IIndustryCodesFilterDto | undefined;
        const scalarDelta = valueDelta as FilterValueDelta<string> | undefined;

        if (!condition) {
            return "added";
        }

        return isInclusionConditionFullyRemoved(getIndustryCodesConditionScalars(condition), scalarDelta)
            ? "removed"
            : "added";
    }
}

export default IndustryCodesFilterModel;
